// src/utils/tempiCommessa.js
// ═══════════════════════════════════════════════════════════════════════════
// TEMPI E ANDAMENTO DELLA COMMESSA — per la Relazione (W405)
// ═══════════════════════════════════════════════════════════════════════════
// Prima la Relazione diceva «in corso e procede regolarmente» anche con la consegna già
// superata e i lavori fermi (es. piattaforma sospesa per mancanza di materiale). Qui si
// leggono i fatti già registrati e si raccontano:
//  • la consegna prevista (data fine della commessa): superata → ritardo di N giorni;
//  • i lavori fermi: sospensioni dei noleggi (con il motivo) e sospensioni annotate nel
//    Giornale di cantiere, criticità ancora aperte del Giornale;
//  • il materiale che manca: richieste al magazzino non ancora evase e ordini ai fornitori
//    non ancora ricevuti;
//  • le attività sospese e i noleggi ancora aperti oltre la fine prevista.
// Funzione pura: niente letture dal database (provata a parte).
// ═══════════════════════════════════════════════════════════════════════════
import { etichettaNoleggio, scadenzaNoleggio } from "./noleggiUtils";

const dataIt = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "");
const giorniTra = (a, b) => Math.round((Date.parse(b + "T00:00:00") - Date.parse(a + "T00:00:00")) / 86400000);
const isoDa = (v) => {
  if (!v) return "";
  if (typeof v === "string") return /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : "";
  const d = v.toDate ? v.toDate() : v instanceof Date ? v : null;
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";
};
const STATI_ODA_IN_ARRIVO = ["Inviato", "Confermato", "Parzialmente ricevuto"];
const STATI_MAGAZZINO_APERTI = ["in_attesa", "in_lavorazione", "parziale"];

/**
 * @returns {{ consegna, ritardoGiorni, giorniAllaConsegna, fermi: string[], storia: string[], materiali: string[],
 *            altri: string[], testo: string, attenzione: string[], sintesi: string|null }}
 */
export function tempiCommessa({ project = {}, aperta = true, oggi, noleggi = [], giornale = [], richieste = [], ordini = [], tasks = [], projectId } = {}) {
  const consegna = isoDa(project.dataFine);
  const ritardoGiorni = aperta && consegna && consegna < oggi ? giorniTra(consegna, oggi) : 0;
  const giorniAllaConsegna = aperta && consegna && consegna >= oggi ? giorniTra(oggi, consegna) : null;

  // ── lavori fermi (adesso) e sospensioni già passate (storia della commessa) ──
  const fermi = [], storia = [];
  noleggi.filter(n => !n.eliminato).forEach(n => {
    (n.sospensioni || []).filter(s => s && s.dal).forEach(s => {
      const motivo = s.motivo ? ` per «${String(s.motivo).trim()}»` : "";
      if (s.dal <= oggi && (!s.al || s.al >= oggi)) fermi.push(`${etichettaNoleggio(n)} sospeso dal ${dataIt(s.dal)}${s.al ? ` al ${dataIt(s.al)}` : ""}${motivo}.`);
      else if (s.dal < oggi) storia.push(`${etichettaNoleggio(n)} sospeso dal ${dataIt(s.dal)} al ${dataIt(s.al)}${motivo}.`);
      else storia.push(`${etichettaNoleggio(n)}: sospensione programmata dal ${dataIt(s.dal)}${motivo}.`);
    });
  });
  const giorniFermi = giornale.filter(g => String(g.sospensioni || "").trim() && isoDa(g.data))
    .sort((a, b) => isoDa(b.data).localeCompare(isoDa(a.data)));
  if (giorniFermi.length) {
    const ultimi = giorniFermi.slice(0, 3).map(g => `${dataIt(isoDa(g.data))}: «${String(g.sospensioni).trim().slice(0, 140)}»`);
    const frase = `Il Giornale di cantiere registra sospensioni dei lavori in ${giorniFermi.length} giorn${giorniFermi.length === 1 ? "o" : "i"} (${ultimi.join("; ")}${giorniFermi.length > 3 ? "; …" : ""}).`;
    // l'ultima sospensione negli ultimi 3 giorni: i lavori sono fermi adesso
    (aperta && giorniTra(isoDa(giorniFermi[0].data), oggi) <= 3 ? fermi : storia).push(frase);
  }
  const criticitaAperte = giornale.flatMap(g => (g.criticita || []).filter(c => c && c.stato !== "risolta" && String(c.descrizione || "").trim()));
  if (aperta && criticitaAperte.length) fermi.push(`${criticitaAperte.length} criticità del Giornale di cantiere ${criticitaAperte.length === 1 ? "risulta ancora aperta" : "risultano ancora aperte"} (es. «${String(criticitaAperte[0].descrizione).trim().slice(0, 120)}»).`);

  // ── materiale che manca ──
  const materiali = [];
  const magazzinoAperte = richieste.filter(r => STATI_MAGAZZINO_APERTI.includes(r.stato));
  if (magazzinoAperte.length) materiali.push(`${magazzinoAperte.length} richiest${magazzinoAperte.length === 1 ? "a" : "e"} di materiale al magazzino non ancora evas${magazzinoAperte.length === 1 ? "a" : "e"} del tutto.`);
  const odaInArrivo = ordini.filter(o => !o.eliminato && (o.commessaId === projectId || o.projectId === projectId) && STATI_ODA_IN_ARRIVO.includes(o.stato));
  if (odaInArrivo.length) materiali.push(`${odaInArrivo.length} ordin${odaInArrivo.length === 1 ? "e" : "i"} ai fornitori non ancora ricevut${odaInArrivo.length === 1 ? "o" : "i"} del tutto.`);

  // ── altro ──
  const altri = [];
  const sospese = tasks.filter(t => t.status === "sospesa");
  if (sospese.length) altri.push(`${sospese.length} attività sospes${sospese.length === 1 ? "a" : "e"}.`);
  noleggi.forEach(n => { const s = scadenzaNoleggio(n, oggi); if (s && s.stato === "oltre") altri.push(`${etichettaNoleggio(n)} ancora aperto oltre la fine prevista (${dataIt(s.prevista)}).`); });

  // ── testo della sezione ──
  const parti = [];
  if (!consegna) parti.push("Data di consegna non indicata sulla Commessa.");
  else if (!aperta) parti.push(`Consegna prevista il ${dataIt(consegna)}.`);
  else if (ritardoGiorni > 0) parti.push(`La consegna era prevista il ${dataIt(consegna)}: la Commessa è in ritardo di ${ritardoGiorni} giorn${ritardoGiorni === 1 ? "o" : "i"}.`);
  else parti.push(giorniAllaConsegna === 0 ? "La consegna è prevista per oggi." : `Consegna prevista il ${dataIt(consegna)}, tra ${giorniAllaConsegna} giorn${giorniAllaConsegna === 1 ? "o" : "i"}.`);
  if (fermi.length) parti.push(`Lavori fermi: ${fermi.join(" ")}`);
  if (storia.length) parti.push(`Nel corso dei lavori: ${storia.join(" ")}`);
  if (aperta && materiali.length) parti.push(`Materiale in attesa: ${materiali.join(" ")}`);
  if (aperta && altri.length) parti.push(altri.join(" "));
  if (aperta && !ritardoGiorni && !fermi.length && !materiali.length && !altri.length && consegna) parti.push("Non risultano sospensioni dei lavori né materiale in attesa.");

  // ── elementi di attenzione e sintesi ──
  const attenzione = [];
  if (ritardoGiorni > 0) attenzione.push(`Consegna prevista superata da ${ritardoGiorni} giorn${ritardoGiorni === 1 ? "o" : "i"} (${dataIt(consegna)}): aggiornare la data di consegna o recuperare il ritardo.`);
  if (fermi.length) attenzione.push(...fermi.map(f => `Lavori fermi — ${f}`));
  if (aperta && materiali.length) attenzione.push(...materiali.map(m => `Materiale in attesa — ${m}`));
  if (aperta) attenzione.push(...altri);

  const causa = fermi.length ? "con i lavori fermi" : (aperta && materiali.length) ? "con materiale ancora in attesa" : storia.length ? "dopo sospensioni dei lavori" : "";
  const sintesi = ritardoGiorni > 0
    ? `Commessa in corso e in ritardo di ${ritardoGiorni} giorn${ritardoGiorni === 1 ? "o" : "i"} sulla consegna prevista del ${dataIt(consegna)}${causa ? `, ${causa}` : ""}.`
    : (aperta && fermi.length) ? "Commessa in corso, con i lavori al momento fermi (vedi Tempi e andamento)."
    : null;

  return { consegna, ritardoGiorni, giorniAllaConsegna, fermi, storia, materiali, altri, testo: parti.join(" "), attenzione, sintesi };
}
